import { EXPLORE_IMAGES } from '../exploreImages';
import { OFFICIAL_SOURCES, LAST_VERIFIED } from '../officialSources';

export const ABC_JOURNEY_BASE = {
	slug: 'annapurna-base-camp',
	title: 'Annapurna Base Camp Journey',
	eyebrow: 'Journey Blueprint',
	subtitle: 'Forest trails to glacier amphitheatre — Nepal\'s best first high-altitude trek.',
	lead: 'Shorter and lower than Everest, but every bit as dramatic. We guide you from first thought to ABC and back home.',
	hero: EXPLORE_IMAGES.annapurna,
	lastVerified: LAST_VERIFIED,
	coverage: 75,
	status: 'complete',
	destination: { slug: 'annapurna-base-camp', name: 'Annapurna Base Camp' },
	district: { slug: 'kaski', name: 'Kaski / Myagdi' },

	quickFacts: [
		{ value: '7–10 days', label: 'Trek duration' },
		{ value: '4,130m', label: 'Max altitude (ABC)' },
		{ value: 'Mar–May, Oct–Nov', label: 'Best seasons' },
		{ value: 'Moderate–hard', label: 'Difficulty' },
		{ value: '$800–1,800', label: 'Budget trek (excl. flights)' },
	],

	primarySource: OFFICIAL_SOURCES.ntnc,
	officialSources: [
		OFFICIAL_SOURCES.ntnc,
		OFFICIAL_SOURCES.ntb,
		OFFICIAL_SOURCES.immigration,
		OFFICIAL_SOURCES.touristPolice,
	],

	faqs: [
		{ q: 'Is ABC good for a first trek in Nepal?', a: 'Yes — it is the most recommended first high-altitude trek. Lower than EBC (4,130m vs 5,364m), shorter, and road-accessible from Pokhara. Still requires fitness and acclimatization.' },
		{ q: 'Do I need TIMS for Annapurna?', a: 'ACAP (Annapurna Conservation Area Permit) is mandatory. TIMS may also be required depending on current NTB policy — verify before travel. Many trekkers obtain both in Pokhara or Kathmandu.' },
		{ q: 'Pokhara or Kathmandu as base?', a: 'Most trekkers fly or bus to Pokhara, then drive/trek from Nayapul or Ghandruk. Allow 1 night in Pokhara before starting the trail.' },
		{ q: 'What altitude does insurance need to cover?', a: 'Minimum 4,500m for ABC. If you add optional peaks (e.g. Machapuchare base), confirm 5,000m+ cover.' },
	],

	related: [
		{ label: 'Annapurna Base Camp destination', to: '/destinations/annapurna-base-camp' },
		{ label: 'Phewa Lake / Pokhara', to: '/destinations/phewa-lake' },
		{ label: 'Trekking permits', to: '/guides/tims-card-trekking-permits' },
		{ label: 'Travel insurance', to: '/guides/travel-insurance-for-nepal-trekking' },
		{ label: 'Altitude sickness', to: '/guides/altitude-sickness-prevention-response' },
		{ label: 'Everest Base Camp journey', to: '/journeys/everest-base-camp' },
		{ label: 'Langtang Valley journey', to: '/journeys/langtang-valley' },
	],

	steps: [
		{
			id: 'dream',
			name: 'Dream',
			icon: '💭',
			question: 'Is Annapurna Base Camp the right trek for me?',
			lead: 'ABC is Nepal\'s classic first trek — dramatic, achievable, and shorter than Everest.',
			sections: [
				{ id: 'why-abc', question: 'Why choose ABC?', answer: 'You walk from subtropical forest through rhododendron hills into a glacier amphitheatre ringed by Annapurna I (8,091m), Machapuchare, and Hiunchuli. Teahouse trek — no camping required. Most fit hikers complete it in 7–10 days.' },
				{ id: 'fitness', question: 'Am I fit enough?', answer: 'You should walk 5–7 hours on hilly terrain comfortably. ABC is strenuous but shorter than EBC. Cardio and stair training for 4–6 weeks before departure is standard.' },
			],
			nextLabel: 'Research the trek →',
		},
		{
			id: 'research',
			name: 'Research',
			icon: '🔍',
			question: 'Season, duration, cost?',
			lead: 'Lock in the basics before booking flights.',
			sections: [
				{ id: 'season', question: 'Best season?', answer: 'October–November: clearest views, busiest. March–April: rhododendrons, warming weather. Avoid monsoon (June–September) — leeches, rain, landslide risk on roads.' },
				{ id: 'cost', question: 'What does it cost?', answer: 'Budget teahouse trek: $25–35/day on trail. Total $800–1,800 excluding international flights. No expensive Lukla flights — access is by road from Pokhara.' },
			],
			nextLabel: 'Get your visa →',
		},
		{
			id: 'visa',
			name: 'Visa',
			icon: '🛂',
			question: 'Nepal tourist visa',
			lead: 'Same visa process as any Nepal trek — 30 days is standard for ABC.',
			sections: [
				{ id: 'visa', question: 'Visa requirements', answer: 'Tourist visa required for most nationalities. 30 days: USD 50. Apply online at nepaliport.immigration.gov.np or visa on arrival at Kathmandu airport.', source: OFFICIAL_SOURCES.immigration },
			],
			nextLabel: 'Book flights →',
		},
		{
			id: 'flights',
			name: 'Flights',
			icon: '✈️',
			question: 'International flights and getting to Pokhara',
			lead: 'Fly to Kathmandu, then bus or domestic flight to Pokhara.',
			sections: [
				{ id: 'intl', question: 'International to Kathmandu', answer: 'Book to Tribhuvan International Airport (KTM). Allow 1–2 days in Kathmandu for permits/gear, then travel to Pokhara (6–7 hr bus or 25 min flight).' },
				{ id: 'pokhara', question: 'Kathmandu to Pokhara', answer: 'Tourist bus ($10–15), private car ($80–120), or domestic flight ($80–120). Most ABC trekkers base in Pokhara one night before the trail.' },
			],
			nextLabel: 'Get insurance →',
		},
		{
			id: 'insurance',
			name: 'Insurance',
			icon: '🛡',
			question: 'Travel insurance for ABC',
			lead: 'Helicopter evacuation and 4,500m altitude cover required.',
			sections: [
				{ id: 'cover', question: 'What to cover?', answer: 'Emergency medical, helicopter evacuation, altitude limit 4,500m minimum. ABC sits at 4,130m but acclimatization hikes go higher. Read exclusions carefully.' },
			],
			nextLabel: 'Pack your bag →',
		},
		{
			id: 'packing',
			name: 'Packing',
			icon: '🎒',
			question: 'Gear for ABC',
			lead: 'Lighter than EBC — but still cold at base camp.',
			sections: [
				{ id: 'essentials', question: 'Essentials', answer: 'Waterproof boots, -10°C sleeping bag, down jacket, rain shell, trekking poles, purification tablets. Nights at ABC are cold (below freezing in peak season).' },
			],
			nextLabel: 'Understand permits →',
		},
		{
			id: 'permits',
			name: 'Permits',
			icon: '📋',
			question: 'ACAP and TIMS permits',
			lead: 'Annapurna Conservation Area Permit is mandatory.',
			sections: [
				{ id: 'acap', question: 'ACAP permit', answer: 'Annapurna Conservation Area Permit required for all trekkers. Foreign nationals: NPR 3,000. Obtain in Kathmandu (NTB) or Pokhara (ACAP office).', source: OFFICIAL_SOURCES.ntnc },
				{ id: 'tims', question: 'TIMS card', answer: 'TIMS may be required — verify current NTB policy. If needed: NPR 2,000 (group) or NPR 4,000 (independent) via TAAN or NTB.', source: OFFICIAL_SOURCES.ntb },
			],
			nextLabel: 'Where to get permits →',
		},
		{
			id: 'permit-offices',
			name: 'Permit Offices',
			icon: '📍',
			question: 'Where to obtain ACAP',
			lead: 'Kathmandu or Pokhara — before you start walking.',
			sections: [
				{ id: 'offices', question: 'Permit offices', answer: 'Kathmandu: Nepal Tourism Board, Bhrikutimandap. Pokhara: ACAP permit counter near Lakeside. Bring passport, photos, and NPR cash.' },
			],
			nextLabel: 'Guide or solo? →',
		},
		{
			id: 'guide',
			name: 'Guide',
			icon: '🧭',
			question: 'Guide required for ABC?',
			lead: 'Classic ABC route is commonly trekked independently — verify current policy.',
			sections: [
				{ id: 'solo', question: 'Independent trekking', answer: 'ABC trail is well-marked with frequent teahouses. Solo and independent trekking is common. Guide optional but helpful in monsoon shoulder season or for first-time trekkers.', source: OFFICIAL_SOURCES.ntb },
			],
			nextLabel: 'Hiring help →',
		},
		{
			id: 'sherpa',
			name: 'Sherpa',
			icon: '🏔',
			question: 'Hiring through agencies',
			lead: 'Book guides via TAAN-registered agencies in Pokhara or Kathmandu.',
			sections: [
				{ id: 'hire', question: 'How to hire', answer: 'Pokhara Lakeside has dozens of trekking agencies. Verify TAAN membership. Guide: $20–30/day for ABC route.' },
			],
			nextLabel: 'Porter logistics →',
		},
		{
			id: 'porter',
			name: 'Porter',
			icon: '📦',
			question: 'Porter for ABC',
			lead: 'Optional but common — carry a light daypack on steep stone steps.',
			sections: [
				{ id: 'porter', question: 'Porter details', answer: 'USD 15–20/day. Max 15 kg duffel. Chhomrong steps are brutal with a heavy pack — porter is worth it.' },
			],
			nextLabel: 'Kathmandu & Pokhara prep →',
		},
		{
			id: 'kathmandu',
			name: 'Kathmandu',
			icon: '🏙',
			question: 'Prep in Kathmandu and Pokhara',
			lead: 'SIM, cash, gear — then move to Pokhara to start the trek.',
			sections: [
				{ id: 'prep', question: 'Pre-trek prep', answer: 'Kathmandu: permits, gear rental/buy in Thamel, SIM card, ATM. Pokhara: lakeside hotels, last-minute supplies, arrange transport to trailhead (Nayapul/Ulleri/Ghandruk).' },
			],
			nextLabel: 'Trailhead access →',
		},
		{
			id: 'lukla',
			name: 'Trailhead',
			icon: '🚐',
			question: 'Getting to the trailhead',
			lead: 'Drive from Pokhara to Nayapul, then trek — no mountain flight needed.',
			sections: [
				{ id: 'access', question: 'Trailhead options', answer: 'Most common: taxi/bus Pokhara → Nayapul (1.5 hr), trek via Ulleri stone steps or Ghandruk route. Alternative: drive to Ghandruk road head. No flight dependency — a major ABC advantage over EBC.' },
			],
			nextLabel: 'Day-by-day trek →',
		},
		{
			id: 'trek',
			name: 'Trek',
			icon: '🥾',
			question: 'Day-by-day to ABC',
			lead: '7–8 days Pokhara round trip via Chhomrong and Machapuchare base.',
			isTrekDays: true,
			sections: [
				{ id: 'route', question: 'Route overview', answer: 'Nayapul → Ulleri/Ghandruk → Chhomrong → Himalaya → ABC → return via same route or Jhinu hot springs. Acclimatize at Chhomrong and Himalaya.' },
			],
			nextLabel: 'Emergency planning →',
		},
		{
			id: 'emergency',
			name: 'Emergency',
			icon: '🚨',
			question: 'AMS and rescue on ABC route',
			lead: 'Lower than EBC but AMS still occurs above 3,000m.',
			sections: [
				{ id: 'ams', question: 'Altitude sickness', answer: 'ABC is 4,130m — AMS is real. Symptoms above Chhomrong (2,170m) need attention. Helicopter evacuation available but slower to arrange than Khumbu. Descend if symptoms worsen.' },
			],
			nextLabel: 'Returning home →',
		},
		{
			id: 'return',
			name: 'Return',
			icon: '🏠',
			question: 'Pokhara, Kathmandu, and home',
			lead: 'Celebrate in Pokhara — lake town recovery before flying home.',
			sections: [
				{ id: 'return', question: 'Heading home', answer: 'Most trekkers return to Pokhara for hot shower and lakeside meal, then bus/flight to Kathmandu for international departure. Build 1 buffer day.' },
			],
		},
	],

	trekDays: [
		{ day: 1, title: 'Pokhara → Nayapul → Ulleri/Ghandruk', sleep: 'Ulleri or Ghandruk (1,940–1,970m)', altitude: '2,000m', distance: '10–12 km', time: '5–7 hrs', food: 'Teahouse', medical: 'Basic', atm: 'Nayapul only', signal: 'Moderate', electricity: 'Yes', water: 'Purified', notes: 'Steep stone steps from Ulleri — pace yourself.' },
		{ day: 2, title: 'Ghandruk → Chhomrong', sleep: 'Chhomrong (2,170m)', altitude: '2,170m', distance: '8 km', time: '5 hrs', food: 'Teahouse', medical: 'Basic clinic', atm: 'No', signal: 'Moderate', electricity: 'Yes', water: 'Purified', notes: 'Views of Machapuchare. Last major village before ABC approach.' },
		{ day: 3, title: 'Chhomrong → Himalaya Hotel', sleep: 'Himalaya (2,920m)', altitude: '2,920m', distance: '10 km', time: '6 hrs', food: 'Teahouse', medical: 'None', atm: 'No', signal: 'Weak', electricity: 'Limited', water: 'Purified', notes: 'Long descent to river, then climb. Jungle gives way to bamboo forest.' },
		{ day: 4, title: 'Himalaya → Annapurna Base Camp', sleep: 'ABC (4,130m)', altitude: '4,130m', distance: '8 km', time: '5–6 hrs', food: 'Teahouse', medical: 'None', atm: 'No', signal: 'Intermittent', electricity: 'Very limited', water: 'Purified', notes: 'Enter glacier amphitheatre — spectacular. Cold night.' },
		{ day: 5, title: 'ABC → Bamboo (descent)', sleep: 'Bamboo (2,310m)', altitude: '4,130m max', distance: '12 km', time: '6 hrs', food: 'Teahouse', medical: 'None', atm: 'No', signal: 'Weak', electricity: 'Limited', water: 'Purified', notes: 'Long descent — knee care on steps.' },
		{ day: 6, title: 'Bamboo → Jhinu Danda (hot springs)', sleep: 'Jhinu (1,780m)', altitude: '1,780m', distance: '10 km', time: '5 hrs', food: 'Teahouse', medical: 'None', atm: 'No', signal: 'Moderate', electricity: 'Yes', water: 'Hot springs!', notes: 'Natural hot springs by river — reward soak.' },
		{ day: 7, title: 'Jhinu → Nayapul → Pokhara', sleep: 'Pokhara (820m)', altitude: '820m', distance: '8 km trek + drive', time: '4 hrs trek', food: 'Full range', medical: 'Hospitals', atm: 'Everywhere', signal: 'Full', electricity: 'Full', water: 'Yes', notes: 'Taxi from Nayapul to Pokhara (1.5 hr).' },
	],
};
